import useTranslation from 'next-translate/useTranslation';

import { displayCurrencyWithPrecision } from '@utils/currency';
import { NO_DATA_DISPLAY } from '@utils/data';
import { parseCellNumber } from './parseCellNumber';

interface UserProvidedBaselineCellProps {
  cellRowClassName: string;
  // The event's currency, passed down by the mounting renderer. Undefined is tolerated and resolves
  // to USD, matching every other price surface.
  currency: string | undefined;
  labelClassName: string;
  priceClassName: string;
  // Required, and deliberately without a default. This cell used to read the precision from company
  // config itself, to stop a defaulted prop from silently falling back to the wrong decimals wherever
  // a mount point forgot to pass it. A required prop cannot be forgotten — TypeScript rejects the call
  // — so it carries none of that risk, and both renderers that mount this already hold the value.
  pricePrecision: number;
  value: unknown;
}

// Presentational cell for a buyer-provided baseline (NSB #3/#7): shows the price (thousands
// separators / currency chars the grid adds to large numbers stripped first) labeled "User Provided",
// with no link and no NSB alternates. Rendered by BOTH the NSP and BPU renderers when
// isUserProvided(sbKey); those cells stay editable, so the buyer can still change the price.
export const UserProvidedBaselineCell = ({
  cellRowClassName,
  currency,
  labelClassName,
  priceClassName,
  pricePrecision,
  value,
}: UserProvidedBaselineCellProps) => {
  const { t } = useTranslation('common');
  // Number("3,102") is NaN, which would wrongly render a dash for values >= 1000 — so strip first.
  const upVal = parseCellNumber(value);
  const showUp = value !== '' && value != null && !Number.isNaN(upVal);

  return (
    <div className={`cell-editor ${cellRowClassName}`}>
      <span className={`line-item-cell-renderer__text ${priceClassName}`}>
        {showUp ? displayCurrencyWithPrecision(upVal, currency, pricePrecision) : NO_DATA_DISPLAY}
      </span>
      <span className={`line-item-cell-renderer__text line-item-cell-renderer__link ${labelClassName}`}>
        {t('line_items.user_provided')}
      </span>
    </div>
  );
};
